import PossibleExemptionType from "models/possibleExemptionType";
import NiaResponse from "models/niaResponse";
import { createEventDefinition, EventBus as TsEventBus } from "ts-bus";
import { ContactEditationTriggerer } from "models/contact";
import ValidChargesResponse from "models/validChargesResponse";
import Vehicle from "models/vehicle";
import { Notice } from "./NIA/niaService";
import { RequestSource } from "./chargeRegistrationService";
import Validity from "models/validity";
import ChargeRegistrationReport from "models/chargeRegistrationReport";
import { KnownErrorCodes } from "config";
import GooglePayMetadata from "models/googlePayMetadata";
import { PayGateCommonPaymentStatus } from "models/googlePayInitPaymentResponse";
import VehicleRegistrationsResponse from "models/vehicleRegistrationsResponse";
import ChargeValidityInstructionsResponse from "entities/chargeValidityInstructionsResponse";
import ChargeRegistrationsResponse from "entities/chargeRegistrationsResponse";
import ChargeValidityInstructionGroupsResponse from "entities/chargeValidityInstructionGroupsResponse";
import ChargeOrderInstructionGroupsResponse from "entities/chargeOrderInstructionGroupsResponse";
import NotificationPreferencesResponse from "entities/notificationPreferencesResponse";
import VehicleChargingInstructionsResponse from "entities/vehicleChargingInstructionsResponse";
import ExemptionApplicationProductionResponse from "entities/exemptionApplicationProductionResponse";
import VehicleChangeApplicationProductionResponse from "entities/vehicleChangeApplicationProductionResponse";
import ExemptionCancellationApplicationProductionResponse from "entities/exemptionCancellationApplicationProductionResponse";
import CustomerRefundApplicationProductionResponse from "entities/customerRefundApplicationProductionResponse";
import ChargesRequest from "entities/chargesRequest";
import ChargeTypeResponse from "entities/chargeTypeResponse";
import ChargeProposedPurchaseResponse from "entities/chargeProposedPurchaseResponse";

export const localeChanged = createEventDefinition<{ locale: string }>()("locale.changed");
export const paymentFailed = createEventDefinition<{ message: string }>()("payment.failed");
export const paymentCompleteAlert = createEventDefinition<{ orderId: string }>()("payment.complete.alert");
export const paymentValidSinceInvalid = createEventDefinition<{ message: string }>()("payment.validSince.invalid");
export const paymentFuelTypeNotAllowed = createEventDefinition<{ message: string }>()("payment.fuelType.notAllowed");
export const paymentMultiFuelTypeNotAllowed = createEventDefinition<{ message: string }>()(
  "payment.multiEshop.fuelType.notAllowed"
);
export const userLogout = createEventDefinition<void>()("user.logout");

export const omissionCreationFailed = createEventDefinition<{ type: string }>()("omission.failed");
export const omissionDocumentsUploadFailed = createEventDefinition<{ type: string }>()("omission.documents.upload.failed");
export const omissionDocumentsUploadSuccess = createEventDefinition<{ conceptIdent: string }>()(
  "omission.documents.upload.success"
);
export const exemptionCreationFailed = createEventDefinition<{ type: string }>()("exemption.failed");
export const exemptionDocumentsUploadFailed = createEventDefinition<{ type: string }>()("exemption.documents.upload.failed");
export const exemptionDocumentsUploadSuccess = createEventDefinition<{ conceptIdent: string }>()(
  "exemption.documents.upload.success"
);
export const licensePlateChangeCreationFailed = createEventDefinition<{ type: string }>()("license.plate.change.failed");
export const licensePlateChangeDocumentsUploadFailed = createEventDefinition<{ type: string }>()(
  "license.plate.change.documents.upload.failed"
);
export const licensePlateChangeDocumentsUploadSuccess = createEventDefinition<{ conceptIdent: string }>()(
  "license.plate.change.documents.upload.success"
);

export const refundRequestCreationFailed = createEventDefinition<{ type: string }>()("refund.request.failed");
export const refundRequestDocumentsUploadFailed = createEventDefinition<{ type: string }>()(
  "refund.request.documents.upload.failed"
);
export const refundRequestDocumentsUploadSuccess = createEventDefinition<{ conceptIdent: string }>()(
  "refund.request.documents.upload.success"
);

export const chargeValidationFailed = createEventDefinition<{ type: string }>()("charge.validation.failed");
export const serverError = createEventDefinition<{ message?: string; type?: KnownErrorCodes }>()("general.serverError");

export const chargeRegistrationReport = createEventDefinition<{ report?: ChargeRegistrationReport }>()(
  "charge.registration.report"
);

export const vehicleRegistration = createEventDefinition<{
  vehicleRegistration: VehicleRegistrationsResponse;
  source?: RequestSource;
}>()("vehicle.registration");

export const vehicleChargeOrderInstructions = createEventDefinition<{
  chargeOrderInstructionGroups?: ChargeOrderInstructionGroupsResponse;
  source?: RequestSource;
}>()("vehicle.vehicle.charge.validity.instructions");

export const vehicleChargingInstructions = createEventDefinition<{
  vehicleChargingInstructions?: VehicleChargingInstructionsResponse;
  source?: RequestSource;
}>()("vehicle.charging.instructions");

export const exemptionApplicationProduction = createEventDefinition<{
  exemptionApplicationProduction?: ExemptionApplicationProductionResponse;
  source?: RequestSource;
}>()("exemption.application.preview");

export const cancellationApplicationProduction = createEventDefinition<{
  cancellationApplicationProduction?: ExemptionCancellationApplicationProductionResponse;
  source?: RequestSource;
}>()("exemption.cancellation.application.preview");

export const customerRefundApplicationProduction = createEventDefinition<{
  customerRefundApplicationProduction?: CustomerRefundApplicationProductionResponse;
  source?: RequestSource;
}>()("exemption.cancellation.application.preview");

export const vehicleChangeApplicationProduction = createEventDefinition<{
  vehicleChangeApplicationProduction?: VehicleChangeApplicationProductionResponse;
  source?: RequestSource;
}>()("vehicle.change.application.preview");

export const vehicleChargeValidityInstructions = createEventDefinition<{
  chargeValidityInstructionGroups?: ChargeValidityInstructionGroupsResponse;
  charges?: ChargesRequest[];
  source?: RequestSource;
}>()("vehicle.vehicle.charge.validity.instructions");

export const chargeRegistration = createEventDefinition<{
  chargeRegistrationsResponse: ChargeRegistrationsResponse;
  source?: RequestSource;
}>()("charge.registration");

export const paymentConfirmationAvailable = createEventDefinition<{ url: string }>()("payment.confirmation.available");
export const paymentConfirmationError = createEventDefinition<void>()("payment.confirmation.error");
export const customerRatingOrderData = createEventDefinition<{
  orderId?: string;
}>()("customerRating.orderData.updated");

export const putChargeContactSuccess = createEventDefinition<{
  emailAddress?: string;
  notificationPreferences?: NotificationPreferencesResponse;
  triggerer: ContactEditationTriggerer;
  authCode?: string;
}>()("put.charge.contact.success");
export const putChargeContactFailure = createEventDefinition<{ type: string; triggerer: ContactEditationTriggerer }>()(
  "put.charge.contact.failure"
);

export const getGooglePayMetadataSuccess = createEventDefinition<{ googlePayMetadata: GooglePayMetadata }>()(
  "get.google.pay.metadata.success"
);
export const getGooglePayMetadataFailure = createEventDefinition<void>()("get.google.pay.metadata.failure");

export const submitGooglePayPaymentSuccess = createEventDefinition<void>()("submit.google.pay.payment.success");
export const submitGooglePayPaymentDeferred = createEventDefinition<void>()("submit.google.pay.payment.deferred");
export const submitGooglePayPaymentFailure = createEventDefinition<void>()("submit.google.pay.payment.failure");
export const submitGooglePayPaymentBrowserFingerprint = createEventDefinition<{ url: string }>()(
  "submit.google.pay.payment.fingerprint"
);
export const googlePayPaymentBrowserFingerprintLoaded = createEventDefinition<void>()("google.pay.payment.fingerprint.loaded");
export const googlePayPayment3DSecureLoaded = createEventDefinition<{ url: string }>()("google.pay.payment.3dsecure.loaded");
export const googlePayPaymentStatusChanged = createEventDefinition<{ status: PayGateCommonPaymentStatus }>()(
  "google.pay.payment.status.changed"
);

export const putChargeValiditySuccess = createEventDefinition<{ validity: Validity }>()("put.charge.validity.success");
export const putChargeValidityFailure = createEventDefinition<{ type: string }>()("put.charge.validity.failure");

export const putChargeVehicleSuccess = createEventDefinition<{ vehicle: Vehicle }>()("put.charge.vehicle.success");
export const putChargeVehicleFailure = createEventDefinition<{ type: string }>()("put.charge.vehicle.failure");

export const sendChargePaymentConfirmationEmailModalClosed = createEventDefinition()(
  "send.charge.payment.confirmation.email.modal.closed"
);
export const sendChargePaymentConfirmationEmailSuccess = createEventDefinition<{}>()(
  "send.charge.payment.confirmation.email.success"
);
export const sendChargePaymentConfirmationEmailFailure = createEventDefinition<{ type: string }>()(
  "send.charge.payment.confirmation.email.failure"
);
export const downloadPaymentConfirmationSuccess = createEventDefinition<{}>()("download.payment.confirmation.success");
export const downloadPaymentConfirmationFailure = createEventDefinition<{ type: string }>()(
  "download.payment.confirmation.failure"
);
export const emailUpdated = createEventDefinition<{ emailAddress: string }>()("email.updated");

export const exemptionISDSConceptStatusCheckSuccess = createEventDefinition<{ success: boolean; notice: Notice }>()(
  "exemption.ISDS.concept.status.check.success"
);
export const exemptionISDSConceptStatusCheckFailure = createEventDefinition<{ type: string; code: string | undefined }>()(
  "exemption.ISDS.concept.status.check.failure"
);

export const omissionISDSConceptStatusCheckSuccess = createEventDefinition<{ success: boolean; notice: Notice }>()(
  "omission.ISDS.concept.status.check.success"
);
export const omissionISDSConceptStatusCheckFailure = createEventDefinition<{ type: string; code: string | undefined }>()(
  "omission.ISDS.concept.status.check.failure"
);

export const licensePlateChangeISDSConceptStatusCheckSuccess = createEventDefinition<{ success: boolean; notice: Notice }>()(
  "license.plate.change.ISDS.concept.status.check.success"
);
export const licensePlateChangeISDSConceptStatusCheckFailure = createEventDefinition<{
  type: string;
  code: string | undefined;
}>()("license.plate.change.ISDS.concept.status.check.failure");

export const refundRequestISDSConceptStatusCheckSuccess = createEventDefinition<{ success: boolean; notice: Notice }>()(
  "refund.request.ISDS.concept.status.check.success"
);
export const refundRequestISDSConceptStatusCheckFailure = createEventDefinition<{
  type: string;
  code: string | undefined;
}>()("refund.request.ISDS.concept.status.check.failure");

export const NIARedirectFailure = createEventDefinition<{ type: string }>()("nia.redirect.failure");

export const getExemptionFailure = createEventDefinition<{ type: string }>()("get.exemption.failure");
export const postExemptionFailure = createEventDefinition<{ type: string }>()("post.exemption.failure");
export const lookForExemptionSuccess = createEventDefinition<{ hasExemption: boolean }>()("look.for.exemption.success");

export const exemptionISDSSessionIdHandled = createEventDefinition<{ sessionId: string }>()("exemption.isds.session.id.handled");
export const exemptionNIAResponseHandled = createEventDefinition<{ niaResponse: NiaResponse }>()(
  "exemption.nia.response.handled"
);

export const omissionISDSSessionIdHandled = createEventDefinition<{ sessionId: string }>()("omission.isds.session.id.handled");
export const omissionNIAResponseHandled = createEventDefinition<{ niaResponse: NiaResponse }>()("omission.nia.response.handled");

export const licensePlateChangeISDSSessionIdHandled = createEventDefinition<{ sessionId: string }>()(
  "license.plate.change.isds.session.id.handled"
);
export const licensePlateChangeNIAResponseHandled = createEventDefinition<{ niaResponse: NiaResponse }>()(
  "license.plate.change.nia.response.handled"
);

export const refundRequestISDSSessionIdHandled = createEventDefinition<{ sessionId: string }>()(
  "refund.request.isds.session.id.handled"
);
export const refundRequestNIAResponseHandled = createEventDefinition<{ niaResponse: NiaResponse }>()(
  "refund.request.nia.response.handled"
);

export const vehicleIsExempted = createEventDefinition<{ source?: RequestSource }>()("vehicle.is.exempted");
export const vehicleIsPossibleExempted = createEventDefinition<{
  exemptionType: PossibleExemptionType;
  source?: RequestSource;
}>()("vehicle.is.possibleExempted");
export const vehicleHasRegisteredCharge = createEventDefinition<{
  chargeRegistration: ChargeRegistrationsResponse;
  source?: RequestSource;
}>()("vehicle.has.registeredCharge");
export const vehicleHasValidCharges = createEventDefinition<{
  validCharges: ValidChargesResponse;
  source?: RequestSource;
}>()("vehicle.has.validCharges");
export const vehicleValidityInstruction = createEventDefinition<{
  validityInstruction: ChargeValidityInstructionsResponse;
  source?: RequestSource;
}>()("vehicle.vehicleValidityInstruction");
export const preselectChargeType = createEventDefinition<{
  chargeType: ChargeTypeResponse;
}>()("vehicle.preselectChargeType");
export const proposedPurchageCharge = createEventDefinition<{
  proposedPurchase: ChargeProposedPurchaseResponse;
}>()("vehicle.proposedPurchageCharge");

export const orderIsPaid = createEventDefinition()("order.paid");
export const orderIsInvalid = createEventDefinition()("order.invalid");
export const multiOrderIsInvalid = createEventDefinition()("order.multi.invalid");

export default class EventBus extends TsEventBus {}
